export interface APIResponse<T=any>{
	error: boolean,
	code: string,
	message: string,
	data?: T & { [x: string]: any },
}

export interface SearchEvent{
	access_code: string,
	admin_id: number
	category_id: number,
	date_created: number,
	date_deleted: number,
	date_publish: number,
	deleted: boolean,
	event_id: number,
	event_name: string,
	event_type: number,
	promoter_id: number,
	published: boolean,
	sinopsis: string,
	subdomain: string,
	next_date: number,
	next_city: string,
}

export interface PaymentMethod{
	method_id: number,
	method_name: string,
	method_name_internal: string,
	commission_percent: number,
	commission_amount: number,
	pdv: boolean,
	internal: boolean,
}

export enum TicketType{
	PHYSICAL = 1,
	DIGITAL = 40,
}

export interface DeliveryMethod{
	delivery_id: number,
	method_name: string,
	ticket_type: TicketType,
	ticket_type_name?: string,
	needs_location: boolean,
	delivery_cost: number,
	deleted?: boolean,
	user_created?: number,
	date_created?: number,
	date_deleted?: number,
}

export enum SystemAccess{
	ADMIN = 2,
	OPERATIONS = 3,
	REPORTS = 5,
	PDV = 20,
	SCANNER = 21,
}

export enum LoginSource{
	PUBLIC = 1,
	ADMIN = 2,
	OPERATIONS = 3,
	REPORTS = 5,
	PDV = 20,
	SCANNER = 21,
	GENERIC = 255,
}

export type LoginSourceTypes = keyof typeof LoginSource;
export type SystemAccessTypes = keyof typeof SystemAccess;

export interface LoginTokenPayload{
	u: number,
	s: number,
	t?: number
}

export enum UserType{
	NORMAL = 1,
	ADMIN = 20,
}

export type SystemAccessType = {
	[P in SystemAccessTypes]?: boolean
}

export interface CityVenues{
	city: string,
	venues: number,
}

export interface EventDate{
	date_id: number,
	event_id: number,
	venue_id: number,
	admin_id: number,
	date: number,
	date_web_start: number,
	date_web_end: number,
	date_pdv_start: number,
	date_pdv_end: number,
	min_tickets: number,
	max_tickets: number,
	card_commission: number,
	active_web: boolean,
	active_pdv: boolean,
	status_id?: number,
	queue_id: number,
	scanner_start: number,
	scanner_end: number,
	refundable_web: boolean,
	refundable_pdv: boolean,
	refundable_complete: boolean,
	deleted: boolean,
	date_created: number,
	date_deleted: number,
	available_web?: boolean,
	available_pdv?: boolean,
	published: boolean,
	venue_name?: string,
	city?: string,
	[x: string]: any,
}

export interface EventDateData extends EventDate{
	city: string,
	venue_name: string,
	delivery?: {
		date_id?: number,
		delivery_id: number,
		ticket_type: number,
		ticket_type_name: string,
		method_name: string,
		needs_location: boolean,
		delivery_cost: number,
		active: boolean,
		user_created?: number,
		date_start: number,
		date_end: number,
		date_created?: number,
	}[]
	payment_methods?: {
		payment_method: number,
		admin_id?: number,
		commission_amount?: number,
		commission_percent?: number,
		date_commission_amount?: number,
		date_commission_percent?: number,
		date_created?: number,
		date_end: number,
		date_id: number,
		date_start: number,
		force_show: boolean,
		active: boolean,
		method_name: string,
		method_name_internal: string,
	}[],
	sections: {
      section_id: number,
      section_name: string,
      numbered: boolean,
		infinite?: boolean,
      description: string,
      map_x?:number,
      map_y?: number,
      map_width?: number,
      map_height?: number,
      seats_width?: number,
      seats_height?: number,
      published: boolean,
      date_start: number,
      date_end: number,
      active: boolean,
      prices_max?: number,
      prices_min?: number,
      prices: {
			section_id?: number,
			price_id: number,
			price_name: string,
			cost: number,
			commission: number,
			multiple: number,
			min_tickets: number,
			max_tickets: number,
			date_start: number,
			date_end: number,
			available: boolean,
			published_web?: boolean,
			published_pdv?: boolean,
			restricted?: boolean,
			days_available?: boolean[],
			ticket_limit?: number,
			needs_comment?: boolean,
		}[],
   }[],
	venue: Venue
}
export interface VenueSeat{
	seat_id: number,
	section_id: number,
	seat_x: number,
	seat_y: number,
	seat_section: string,
	seat_row: string,
	seat_number: string,
	seat_status: number,
	venue_seat_status?: number,
	ticket_id?: number,
	disabled?: number,
	date_created?: number,
	date_disabled?: number,
	seat_cost?: number,
	seat_commission?: number,
	status_comment?: string,
}

export interface VenueSection{
	section_id: number,
	venue_id: number,
	section_name: string,
	ticket_count: number,
	seat_count?: number,
	numbered: boolean,
	seats_width?: number,
	seats_height?: number,
	stage_x?: number,
	stage_y?: number,
	stage_width?: number,
	stage_height?: number,
	map_x?: number,
	map_y?: number,
	map_width?: number,
	map_height?: number,
	infinite: boolean,
	description: string,
	deleted?: boolean,
	user_creator?: number,
	user_creator_name?: string,
	user_deleted?: number,
	user_deleted_name?: string,
	date_created?: number,
	date_deleted?: number,
	seats?: VenueSeat[]
}

export interface Venue{
	venue_id: number,
	venue_name: string,
	location_id: number,
	location_name?: string,
	city?: string,
	user_creator?: number,
	user_creator_name?: string,
	user_deleted?: number,
	user_deleted_name?: string,
	deleted?: boolean,
	date_created?: number,
	date_deleted?: number,
	location?: Location,
	zone_id: number,
	zone_name?: string,
	sections?: VenueSection[],
}

export type SetLoading = (l: boolean)=>void;

export enum QuestionType{
	OPEN = 1,
	TEXTBOX = 2,
	SELECTION = 10,
	MULTIPLE = 11,
	YESNO = 12,
	RATING = 13,
	FILE_DOWNLOAD = 30,
	FILE_IMAGE = 31,
	FILE_OTHER = 32,
	DATE = 50,
	STATIC_TEXT = 90,
}

export interface TicketQuestion{
	answer?: string,
	answer_id?: number,
	comment: string,
	default_value: string,
	question: string,
	question_id: number,
	question_type: QuestionType,
	required: boolean,
	sort_order: number,
	value_id?: number,
	selected?: number[],
	files?: File[]
	values: {
		sort_order: number,
		value_id: number,
		value: string,
	}[]
}

export interface TicketFormPayload{
	question_id: number,
	required: boolean,
	answer?: string,
	selected?: number[],
	files?: File[],
	empty?: boolean,
}

export enum SeatStatus{
	FREE = 1,
	FREE_PDV = 2,
	BLOCKED = 20,
	LOCKED = 21,
	HIDDEN = 40,
}

export interface SeatmapSeat{
	seat_id: number,
	seat_x: number,
	seat_y: number,
	seat_section?: string,
	seat_row?: string,
	seat_number?: string,
	seat_status?: number,
	status_comment?: string,
	ticket_id?: number,
	special?: boolean,
	stage?: boolean,
	seat_cost?: number,
}

export interface SectionSeatmap{
	section_id: number,
	seats_width: number,
	seats_height: number,
	stage_x: number,
	stage_y: number,
	stage_width: number,
	stage_height: number,
	seats: SeatmapSeat[]
}

export interface Location {
	location_id?: number,
	name?: string,
	phone?: string,
	street: string,
	exterior_number: string,
	interior_number?: string,
	neighborhood: string,
	city: string,
	state: string,
	zipcode: string,
	country: string,
	lattitude?: number,
	longitude?: number,
	place_id?: string,
	comments?: string
}

export interface CatalogEvent{
	event_id: number,
	event_name: string,
	subdomain: string,
	category_id?: number,
	category_name: string,
	date: number,
	venue_id?: number,
	venue_name: string,
	city: string,
	state: string,
	poster?: string,
}

export interface LegacyCatalogEvent{
	idevento: number,
	evento_nombre: string,
	primera_fecha: number,
	ciudad: string,
	categoria: string,
	recinto: string,
	poster: string,
}

export interface CatalogEventGroup{
	group_id: number,
	group_name: string,
	category_id?: string,
	category_name: string,
	subdomain: string,
	date: number,
	events: number,
	poster: string,
}

export interface LegacyCatalogEventGroup{
	idpadre: number,
	nombre: string,
	subdominio: string,
	primera_fecha: number,
	ciudad: string,
	categoria: string,
	eventos: number,
	poster: string,
}

export enum DepositType{
	SPEI = 1,
	CARD = 2,
}

export interface BankTransferData{
	email: string,
	type: DepositType,
	account_owner: string,
	clabe: string,
	card_number: string,
	bank: string,
}

export interface Promoter {
	promoter_id: number,
	promoter_name: string,
}

export interface ShortUser{
	user_id: number,
	email: string,
	first_name: string,
	last_name: string,
	name: string,
	superadmin: boolean,
	user_type: UserType,
}

export interface BankAccount{
	account_id?: number,
	name_account: string,
	rfc: string,
	curp?: string,
	bank?: string,
	bank_participant?: number,
	clabe: string,
	card_number: string,
	postal_code?: string,
	email_account?: string,
}

export interface Coupon {
	coupon_id?: number,
	coupon_code: string,
	description: string,
	type_id: number,
	param1: number,
	param2: number,
	active: boolean,
	date_start: number,
	date_end: number,
}

export enum CouponType{
	AMOUNT = 1,
	PERCENT = 2,
	X_PER_X = 10,
	X_PER_MONEY = 11,
}

export interface OrderDownload{
	order_hash: string,
	download_token: string,
	tickets_total: number,
	paid: boolean,
	first_name: string,
	last_name: string,
	email: string,
	date_paid: number,
	date_created: number,
	tickets: {
		ticket_hash: string,
		event_id: number,
		event_name: string,
		date: number,
		ticket_date: number,
		section_name: string,
		price_name: string,
		numbered: boolean,
		seat_row: string,
		seat_number: string,
		seat_section: string,
		download_token: string,
	}[],
	payments?: {
		payment_id: number,
		payment_method: number,
		reference: string,
		paid: boolean,
		total: number,
		date_paid: number,
		date_expiration: number,
		transfer_data?: {
			bank: string,
			concept: string,
			name: string,
			clabe: string,
			agreement: string,
		},
	}[]
}

export interface SearchEventGroup {
	group_id: boolean,
	group_name: string,
	description: string,
	subdomain: string,
}

export interface EventGroup {
	group_id?: number,
	group_name: string,
	subdomain: string,
	published: boolean,
	description: string,
	date_publish: number,
}

export enum DateStatus{
	AVAILABLE = 1,
	NOT_AVAILABLE = 2,
	ONLY_PDV = 3,
	SOLD_OUT = 4,
	CLOSED = 5,
	CANCELLED = 6,
}

export enum SectionStatus{
	AVAILABLE = 1,
	SOLD_OUT = 2,
}

export interface EventCatalogResponse{
	events: CatalogEvent[],
	groups: CatalogEventGroup[],
	legacy_events: LegacyCatalogEvent[],
	legacy_groups: LegacyCatalogEventGroup[],
}

export enum EventType{
	NORMAL = 1,
	CALENDAR = 2,
}

export interface PublicEvent{
	event_id: number,
	event_name: string,
	sinopsis: string,
	date_start: number,
	event_type: number,
	poster?: string,
	access_code: boolean,
	dates: {
		date_id: number,
		date: number,
		active: number,
		status_id?: number,
		venue_id: number,
		venue_name: string,
		city: string,
		place_id: string,
		lattitude: string,
		longitude: string,
	}[]
}

export interface PublicEventGroup{
	group_id: number,
	group_name: string,
	description: string,
	poster?: string,
	events: CatalogEvent[]
}

export interface SubdomainResponse{
	subdomain: string,
	event_id: number,
	group_id: number,
	legacy: boolean,
}

export interface SearchEvent{
	event_id: number,
	legacy?: boolean,
	poster?: string,
	event_name: string,
	venue_name: string,
	city: string,
	state: string,
}

export enum PaymentMethods{
	PAYFRONT = 1,
	OPENPAY_CARD = 2,
	CONEKTA_OXXO = 20,
	OPENPAY_SPEI = 40,
	PDV_CASH = 100,
	PDV_PINPAD_AREMA = 20000,
	PDV_PINPAD_EXTERNAL = 29999,
	FREE = 64000,
	UNKNOWN = 64999,
}

export enum PaymentMethodType{
	CARD = 1,
	CASH = 2,
	OTHER = 100,
}

export interface PaymentPlan {
	plan_id: number,
	months: number,
	interest: boolean,
	minimum_payment: number,
	description: string,
}

export interface EventPaymentPlan {
	event_id: number,
	plan_id: number,
	months: number,
	interest: boolean,
	minimum_payment: number,
	description: string,
	user_added: number,
	user_added_name: string,
}

export interface SearchEventsResult{
	count: number,
	max_count: number,
	date_update: number,
	events: SearchEvent[]
}